import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { financeApi, FinanceInsight } from '../../api/financeApi';
import CollapsibleChartSection from './CollapsibleChartSection';
import InsightCard from './InsightCard';
import InsightDrawer from './InsightDrawer';

/**
 * Insights tile — top of FinanceDashboard. Lazy-mounted via CollapsibleChartSection;
 * the data fetch only fires the first time the tile is expanded. Auto-expands when
 * any critical insight exists.
 */
const InsightsList: React.FC<{ defaultExpanded?: boolean }> = ({ defaultExpanded }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [insights, setInsights] = useState<FinanceInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [active, setActive] = useState<FinanceInsight | null>(null);
  const [counts, setCounts] = useState<{ critical: number; warn: number; total: number }>({
    critical: 0,
    warn: 0,
    total: 0,
  });
  const [headerCounts, setHeaderCounts] = useState<{ critical: number; total: number } | null>(null);
  const fetchedRef = useRef(false);

  // Lightweight counts call to drive header — runs always, even when collapsed.
  useEffect(() => {
    let cancelled = false;
    financeApi
      .getInsightCounts()
      .then((res) => {
        if (cancelled) return;
        const data = res.data || { critical: 0, warn: 0, info: 0, total: 0 };
        setHeaderCounts({ critical: data.critical, total: data.total });
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeApi.getInsights();
      const list = res.data || [];
      setInsights(list);
      const c = list.reduce(
        (acc, i) => {
          if (i.severity === 'CRITICAL') acc.critical += 1;
          else if (i.severity === 'WARN') acc.warn += 1;
          acc.total += 1;
          return acc;
        },
        { critical: 0, warn: 0, total: 0 },
      );
      setCounts(c);
      setHeaderCounts({ critical: c.critical, total: c.total });
    } catch {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, []);

  const onFirstExpand = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchInsights();
  }, [fetchInsights]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await financeApi.refreshInsights();
      if (res.data?.refreshed) {
        toast.success('Insights refreshed');
      } else {
        toast('Refreshed recently — try again in a minute', { icon: '⏳' });
      }
      await fetchInsights();
    } catch {
      toast.error('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, [fetchInsights]);

  const removeLocally = (id: number) => {
    setInsights((prev) => prev.filter((i) => i.id !== id));
  };

  const handleDismiss = useCallback(async (id: number) => {
    removeLocally(id);
    try {
      await financeApi.dismissInsight(id);
    } catch {
      toast.error('Dismiss failed');
      fetchInsights();
    }
  }, [fetchInsights]);

  const handleSnooze = useCallback(async (id: number, days: number) => {
    removeLocally(id);
    try {
      await financeApi.snoozeInsight(id, days);
      toast.success(`Snoozed for ${days} days`);
    } catch {
      toast.error('Snooze failed');
      fetchInsights();
    }
  }, [fetchInsights]);

  const handleHideType = useCallback(
    async (type: string) => {
      try {
        await financeApi.hideInsightType(type, 30);
        toast.success('Insight type hidden for 30 days');
        fetchInsights();
      } catch {
        toast.error('Hide failed');
      }
    },
    [fetchInsights],
  );

  const handleAct = useCallback(
    async (insight: FinanceInsight) => {
      try {
        await financeApi.actInsight(insight.id);
      } catch {
        /* non-blocking */
      }
      const dl = buildDeeplink(insight);
      if (dl) navigate(dl);
      setActive(null);
    },
    [navigate],
  );

  const titleSuffix = useMemo(() => {
    const c = headerCounts ?? counts;
    if (c.total === 0) return ' (all clear)';
    if (c.critical > 0) return ` (${c.critical} critical · ${c.total} total)`;
    return ` (${c.total})`;
  }, [headerCounts, counts]);

  const shouldDefaultExpand = defaultExpanded ?? ((headerCounts?.critical ?? 0) > 0);

  return (
    <>
      <CollapsibleChartSection
        title={`Insights${titleSuffix}`}
        subtitle="Patterns and anomalies in your finance data"
        defaultExpanded={shouldDefaultExpand}
        actions={
          <Tooltip title="Re-run detectors (rate-limited)">
            <span>
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={refreshing}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <ArrowPathIcon
                  style={{
                    width: 18,
                    height: 18,
                    color: theme.gray?.[500],
                    animation: refreshing ? 'spin 1s linear infinite' : undefined,
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        {() => {
          onFirstExpand();
          if (loading) {
            return (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={70} />
                <Skeleton variant="rounded" height={70} />
                <Skeleton variant="rounded" height={70} />
              </Stack>
            );
          }
          if (insights.length === 0) {
            return (
              <Box sx={{ textAlign: 'center', py: 3, color: theme.gray?.[500] }}>
                <Typography variant="body2">All clear — nothing needs attention.</Typography>
                <Typography variant="caption">Detectors run nightly. Hit refresh to re-check now.</Typography>
              </Box>
            );
          }
          return (
            <Stack spacing={1} sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
              {insights.map((i) => (
                <InsightCard
                  key={i.id}
                  insight={i}
                  onOpen={setActive}
                  onAct={handleAct}
                  onDismiss={handleDismiss}
                  onSnooze={handleSnooze}
                  onHideType={handleHideType}
                />
              ))}
            </Stack>
          );
        }}
      </CollapsibleChartSection>

      <InsightDrawer
        insight={active}
        onClose={() => setActive(null)}
        onAct={handleAct}
        onDismiss={(id) => {
          handleDismiss(id);
          setActive(null);
        }}
        onSnooze={(id, days) => {
          handleSnooze(id, days);
          setActive(null);
        }}
        onHideType={(type) => {
          handleHideType(type);
          setActive(null);
        }}
      />

      <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
    </>
  );
};

/**
 * Maps each detector's actionType + actionPayload to a frontend route. Falls back
 * to the relevant finance section when no specific deeplink applies.
 */
function buildDeeplink(insight: FinanceInsight): string | null {
  const p = insight.actionPayload || {};
  switch (insight.actionType) {
    case 'REVIEW_EOD_CLOSES':
      return `/vendor/finance/eod-history${p.closedBy ? `?closedBy=${encodeURIComponent(String(p.closedBy))}` : ''}`;
    case 'COMPARE_EXPENSES':
      return `/vendor/finance/expenses${p.expenseIds ? `?ids=${encodeURIComponent(String(p.expenseIds))}` : ''}`;
    case 'OPEN_STAFF_PAYMENTS':
      return `/vendor/finance/staff-payments`;
    case 'OPEN_RECURRING_TEMPLATE':
      return `/vendor/finance/expenses?source=recurring${p.templateId ? `&templateId=${p.templateId}` : ''}`;
    case 'OPEN_STAFF_PROFILE':
      return `/vendor/finance/staff-payments${p.staffId ? `?staffId=${p.staffId}` : ''}`;
    case 'OPEN_CATEGORY':
      return `/vendor/finance/expenses${p.categoryId ? `?categoryId=${p.categoryId}` : ''}`;
    default:
      return '/vendor/finance/dashboard';
  }
}

export default InsightsList;
